import React, { useEffect, useRef } from 'react';
import { useGoogleMaps } from '../../context/GoogleMapsContext';

interface GooglePlaceAutoCompleteProps {
  id: string;
  value: string;
  extraClass?: string;
  label?: string;
  placeholder?: string;
  rules?: string;
  disabled?: boolean;
  validate?: boolean;
  onChange: (value: string) => void;
  onPlaceSelected?: (place: google.maps.places.PlaceResult) => void;
  onLandmarkLga?: (landmark: string) => void;
  onBlur?: () => void;
}

const GooglePlaceAutoComplete: React.FC<GooglePlaceAutoCompleteProps> = ({
  id,
  value,
  extraClass,
  label,
  placeholder,
  rules,
  disabled,
  onChange,
  onPlaceSelected,
  onLandmarkLga,
  onBlur,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { isLoaded, loadError } = useGoogleMaps();

  useEffect(() => {
    if (inputRef.current && window.google) {
      const autocomplete = new window.google.maps.places.Autocomplete(
        inputRef.current,
      );

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.geometry) {
          const addressComponents = place.address_components;
          const landmark = addressComponents?.find(
            (x) =>
              x.types.includes('route') ||
              x.types.includes('administrative_area_level_2'),
          );

          if (landmark && onLandmarkLga) {
            onLandmarkLga(landmark.long_name);
          }

          if (onPlaceSelected) {
            onPlaceSelected(place);
          }
        }
      });
    }
  }, [onLandmarkLga, onPlaceSelected]);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const containerClass = extraClass || 'mb-6';
  const inputClass = `
    hover:outline-none focus:outline-none
    bg-[#FAFAFA]
    dark:text-black px-4 py-2.5 
    mb-1 rounded-xl w-full 
    hover:border-primary text-sm h-12
    placeholder:text-sm
    ${disabled ? 'cursor-not-allowed bg-gray-100' : ''}
  `;

  if (!isLoaded) {
    return <div>Loading...</div>;
  }

  if (loadError) {
    return <div>Error loading Google Maps</div>;
  }

  return (
    <div className={containerClass}>
      {label && (
        <div className="flex items-center gap-2 pb-2">
          <p className="font-semibold text-xs">{label}</p>
          {rules?.includes('required') && <p className="text-red-500"> *</p>}
        </div>
      )}

      <input
        ref={inputRef}
        id={`autocomplete-${id}`}
        value={value}
        type="text"
        disabled={disabled}
        placeholder={placeholder || label}
        style={{ letterSpacing: '0.5px' }}
        className={inputClass}
        onChange={handleInput}
        onBlur={onBlur}
      />
    </div>
  );
};

export default GooglePlaceAutoComplete;
